<template>
	<div v-if="isIMDAdminCompany || (isUserAllowedInLevel3 && isAuthenticatedUserInGroupAdmin)">
		<BaseHeader :title="$t('pdf_templates').toString()" />

		<div v-if="!isLoading" class="main">
			<div class="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
				<div class="inline-flex items-center">
					<BaseButton
						v-for="(filter, index) in sourceFilters"
						:key="filter.value"
						:class="[
							selectedSource === filter.value ? 'btn-blue' : '',
							index > 0 ? '-ml-px' : '',
							index === 0 ? 'rounded-r-none' : '',
							index > 0 && index < sourceFilters.length - 1 ? 'rounded-none' : '',
							index === sourceFilters.length - 1 ? 'rounded-l-none' : '',
						]"
						@click="selectedSource = filter.value"
					>
						{{ filter.label }}
					</BaseButton>
				</div>

				<BaseInput
					v-model="searchValue"
					:filter-only="true"
					:placeholder="$t('document_definitions.search_placeholder').toString()"
					class="w-full lg:w-80"
				/>
			</div>

			<div class="flex flex-col">
				<div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
					<div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
						<div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
							<table v-if="filteredDocumentDefinitions.length" class="min-w-full table-fixed divide-y divide-gray-200">
								<colgroup>
									<col style="width: 40%" />
									<col style="width: 40%" />
									<col style="width: 20%" />
								</colgroup>
								<thead>
									<tr>
										<th class="py-3 bg-gray-50 text-left text-sm leading-4 font-medium text-gray-500 uppercase tracking-wider px-6 ">
											{{ $t('document_definitions.columns.document') }}
										</th>

										<th class="py-3 bg-gray-50 text-left text-sm leading-4 font-medium text-gray-500 uppercase tracking-wider px-6">
											{{
												selectedSource === 'all' ? $t('document_definitions.columns.active_template') : $t('document_definitions.columns.template')
											}}
										</th>

										<th class="py-3 bg-gray-50 text-left text-sm leading-4 font-medium text-gray-500 uppercase tracking-wider px-6">
											<span class="sr-only">
												{{ $t('document_definitions.columns.actions') }}
											</span>
										</th>
									</tr>
								</thead>

								<tbody class="bg-white divide-y divide-gray-200">
									<tr
										v-for="documentDefinition in filteredDocumentDefinitions"
										:key="documentDefinition.key"
										class="hover:bg-gray-300 cursor-pointer"
										@click="openDocumentDefinition(documentDefinition)"
									>
										<td class="px-6 py-4 text-left">
											<div class="text-md leading-5 font-medium text-gray-900 font-bold">
												{{ documentDefinition.label }}
											</div>

											<!-- <div class="text-sm leading-5 text-gray-500">
												{{ documentDefinition.key }}
											</div> -->
										</td>

										<td class="px-6 py-4 text-left align-middle">
											<!-- Alle -->
											<template v-if="selectedSource === 'all'">
												<div class="flex flex-col justify-center min-h-10">
													<span
														class="text-md leading-5 font-medium"
														:class="documentDefinition.effective_source === 'company' ? 'text-connectProBlue' : 'text-gray-900'"
													>
														{{
															documentDefinition.effective_source === 'company'
																? $t('document_definitions.status.company_in_use')
																: $t('document_definitions.status.system_in_use')
														}}
													</span>

													<span
														v-if="documentDefinition.company_override && !documentDefinition.company_override.is_active"
														class="text-sm leading-5 text-gray-500"
													>
														{{ $t('document_definitions.status.company_disabled') }}
													</span>
												</div>
											</template>

											<!-- Systemvorlagen -->
											<template v-else-if="selectedSource === 'system'">
												<div class="flex flex-col justify-center min-h-10">
													<span class="text-md leading-5 font-medium text-gray-900">
														{{ $t('document_definitions.source.system') }}
													</span>

													<span v-if="documentDefinition.company_override" class="text-sm leading-5 text-gray-500">
														{{ $t('document_definitions.status.company_exists') }}
													</span>
												</div>
											</template>

											<!-- Eigene Vorlagen -->
											<template v-else>
												<div class="flex flex-col justify-center min-h-10">
													<span class="text-md leading-5 font-medium text-connectProBlue">
														{{ $t('document_definitions.source.company') }}
													</span>

													<span class="text-sm leading-5" :class="documentDefinition.company_override.is_active ? 'text-green-700' : 'text-gray-500'">
														{{
															documentDefinition.company_override.is_active
																? $t('document_definitions.status.active')
																: $t('document_definitions.status.inactive_system_used')
														}}
													</span>
												</div>
											</template>
										</td>
										<td class="px-6 py-4 whitespace-no-wrap text-right">
											<div class="flex items-center justify-end">
												<Toggle
													v-if="documentDefinition.company_override"
													:active="documentDefinition.company_override.is_active"
													:disabled="isUpdatingOverride(documentDefinition.key)"
													@click.stop="toggleOverride(documentDefinition)"
												/>

												<font-awesome-icon :icon="['fas', 'chevron-right']" class="ml-5 cursor-pointer" />
											</div>
										</td>
									</tr>
								</tbody>
							</table>
							<div v-else class="min-w-full p-8 text-center bg-white">
								<div class="font-medium text-gray-700">
									{{ selectedSource === 'company' ? $t('document_definitions.empty.company') : $t('document_definitions.empty.default') }}
								</div>

								<div v-if="searchValue || selectedSource !== 'all'" class="mt-1 text-sm text-gray-500">
									{{ $t('document_definitions.empty.adjust_filter') }}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div v-if="documentDefinitions.length" class="flex items-center justify-between mt-4 text-sm text-gray-500">
				<div>
					{{
						$t('document_definitions.result_count', {
							filtered: filteredDocumentDefinitions.length,
							total: documentDefinitions.length,
						})
					}}
				</div>
			</div>
		</div>

		<LoadingSpinner v-else />
	</div>

	<Forbidden v-else />
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import BaseCard from '@/components/damagecases/forms/BaseCard.vue';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import HeaderColumn from '@/components/columns/HeaderColumn.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import Forbidden from '@/views/default/Forbidden.vue';
import Toggle from '@/components/forms/Toggle.vue';

import RouteWatcher from '@/mixins/RouteWatcher';
import Authentication from '@/mixins/Authentication';
import AppContainerMixin from '@/mixins/AppContainerMixin';

import DocumentDefinitionRepository from '@/api/repositories/DocumentDefinitionRepository';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';

export default {
	name: 'DocumentDefinitionsOverview',

	components: {
		BaseButton,
		BaseHeader,
		BaseCard,
		BaseInput,
		HeaderColumn,
		LoadingSpinner,
		Forbidden,
		Toggle,
	},

	mixins: [RouteWatcher, Authentication, AppContainerMixin],

	data() {
		return {
			documentDefinitions: [],

			isLoading: false,

			searchValue: '',

			selectedSource: 'all',

			updatingOverrideKeys: [],

			sourceFilters: [
				{
					value: 'all',
					label: 'Alle',
				},
				{
					value: 'system',
					label: 'Systemvorlagen',
				},
				{
					value: 'company',
					label: 'Eigene Vorlagen',
				},
			],
		};
	},

	computed: {
		filteredDocumentDefinitions() {
			const search = this.searchValue.trim().toLowerCase();

			return this.documentDefinitions.filter(documentDefinition => {
				const label = documentDefinition.label || '';

				const key = documentDefinition.key || '';

				const matchesSearch = !search || label.toLowerCase().includes(search) || key.toLowerCase().includes(search);

				if (!matchesSearch) {
					return false;
				}

				if (this.selectedSource === 'company') {
					return !!documentDefinition.company_override;
				}

				return true;
			});
		},
	},

	methods: {
		onRouteChange() {
			if (this.isUserAllowedInLevel2 || (this.isUserAllowedInLevel3 && this.isAuthenticatedUserInGroupAdmin)) {
				this.getDocumentDefinitions();
			}
		},

		getDocumentDefinitions() {
			this.isLoading = true;

			new DocumentDefinitionRepository()
				.getAll()
				.then(response => {
					this.documentDefinitions = Array.isArray(response.data) ? response.data : [];
				})
				.catch(error => {
					this.documentDefinitions = [];
					this.$swal(this.$t('general.error').toString(), this.$t('values_cannot_be_fetched').toString(), 'error');
				})
				.finally(() => {
					this.isLoading = false;
				});
		},

		redirectToTarget(key, source) {
			this.$router.push({
				name: 'DocumentDefinitionsEdit',

				params: {
					key,
				},

				query: {
					source,
				},
			});
		},

		isUpdatingOverride(key) {
			return this.updatingOverrideKeys.includes(key);
		},

		setOverrideUpdating(key, isUpdating) {
			if (isUpdating) {
				if (!this.updatingOverrideKeys.includes(key)) {
					this.updatingOverrideKeys.push(key);
				}

				return;
			}
			this.updatingOverrideKeys = this.updatingOverrideKeys.filter(entry => entry !== key);
		},

		toggleOverride(documentDefinition) {
			const override = documentDefinition.company_override;

			if (!override || this.isUpdatingOverride(documentDefinition.key)) {
				return;
			}

			const nextIsActive = !override.is_active;

			this.setOverrideUpdating(documentDefinition.key, true);

			new DocumentDefinitionRepository()
				.updateOverrideStatus(documentDefinition.key, nextIsActive)
				.then(response => {
					if (response && response.data && typeof response.data.is_active === 'boolean') {
						override.is_active = response.data.is_active;
					} else {
						override.is_active = nextIsActive;
					}

					documentDefinition.effective_source = override.is_active ? 'company' : 'system';

					this.$swal(
						this.$t('general.success').toString(),

						override.is_active
							? 'Die eigene Vorlage wird jetzt verwendet.'
							: 'Die eigene Vorlage wurde deaktiviert. Die Systemvorlage wird jetzt verwendet.',

						'success',
					);
				})
				.catch(error => {
					console.log(error);

					this.$swal(
						this.$t('general.error').toString(),

						this.$t('general.changes_not_saved').toString(),

						'error',
					);
				})
				.finally(() => {
					this.setOverrideUpdating(documentDefinition.key, false);
				});
		},

		openDocumentDefinition(documentDefinition) {
			let source = 'system';
			if (this.selectedSource === 'company') {
				source = 'company';
			} else if (this.selectedSource === 'all') {
				source = documentDefinition.effective_source === 'company' ? 'company' : 'system';
			}
			this.$router.push({
				name: 'DocumentDefinitionsEdit',
				params: {
					key: documentDefinition.key,
				},
				query: {
					source,
				},
			});
		},
	},
};
</script>

<style scoped>
.document-version-row {
	display: flex;
	align-items: center;
	justify-content: space-between;

	min-height: 42px;
	padding-right: 16px;

	cursor: pointer;
}

.document-version-row-separated {
	margin-top: 12px;
	padding-top: 12px;

	border-top: 1px solid #e5e7eb;
}

.document-version-row:hover {
	opacity: 0.8;
}

.document-no-override {
	margin-top: 12px;
	padding-top: 12px;

	border-top: 1px solid #e5e7eb;

	font-size: 12px;
	color: #6b7280;
}
</style>
